import type { ReportsDebugRow, ValidationMessage } from './xbrl-engine.types';

export type ValidateProgressMessage = {
  type: 'validate-progress';
  id: string;
  phase: string;
  elapsedMs: number;
};

export type ValidateResultMessage = {
  type: 'validate-result';
  id: string;
  counts: { error: number; warning: number; info: number; total: number };
  messages: ValidationMessage[];
  truncatedAt: number | null;
  totalMs: number;
  validateFailed: boolean;
};

export type ValidateErrorMessage = {
  type: 'validate-error';
  id: string;
  phase: string;
  error: string;
};

export type ReportsProgressMessage = {
  type: 'reports-progress';
  id: string;
  phase: string;
  elapsedMs: number;
};

export type ReportsResultMessage = {
  type: 'reports-result';
  id: string;
  downloadFilename: string;
  xlsxBytes: Uint8Array;
  debugRows?: ReportsDebugRow[];
  totalMs: number;
  tableCount: number;
};

export type ReportsErrorMessage = {
  type: 'reports-error';
  id: string;
  phase: string;
  error: string;
};

export type WorkerJobMessage =
  | ValidateProgressMessage
  | ValidateResultMessage
  | ValidateErrorMessage
  | ReportsProgressMessage
  | ReportsResultMessage
  | ReportsErrorMessage;

export function asWorkerJobMessage(data: unknown): WorkerJobMessage | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const type = record['type'];
  const id = record['id'];
  if (typeof type !== 'string' || typeof id !== 'string') return null;

  switch (type) {
    case 'validate-progress':
    case 'validate-result':
    case 'validate-error':
    case 'reports-progress':
    case 'reports-result':
    case 'reports-error':
      return data as WorkerJobMessage;
    default:
      return null;
  }
}

export function isValidateProgress(
  msg: WorkerJobMessage,
  id: string,
): msg is ValidateProgressMessage {
  return msg.type === 'validate-progress' && msg.id === id;
}

export function isValidateResult(
  msg: WorkerJobMessage,
  id: string,
): msg is ValidateResultMessage {
  return msg.type === 'validate-result' && msg.id === id;
}

export function isValidateError(
  msg: WorkerJobMessage,
  id: string,
): msg is ValidateErrorMessage {
  return msg.type === 'validate-error' && msg.id === id;
}

export function isReportsProgress(
  msg: WorkerJobMessage,
  id: string,
): msg is ReportsProgressMessage {
  return msg.type === 'reports-progress' && msg.id === id;
}

export function isReportsResult(
  msg: WorkerJobMessage,
  id: string,
): msg is ReportsResultMessage {
  return msg.type === 'reports-result' && msg.id === id;
}

export function isReportsError(msg: WorkerJobMessage, id: string): msg is ReportsErrorMessage {
  return msg.type === 'reports-error' && msg.id === id;
}
